import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Users,
  Ticket,
  Activity,
  Globe,
  Phone,
  MapPin,
  Edit2,
  Trash2,
  MoreVertical,
  ExternalLink,
  Clock,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Search,
  Plus,
  ChevronRight,
} from 'lucide-react';
import {
  Button,
  Card,
  Badge,
  Spinner,
  Avatar,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  EmptyState,
  Pagination,
  Dropdown,
  MenuItem,
  MenuDivider,
  ConfirmDialog,
} from '@centraoneplus/ui';
import {
  organizationService,
  Organization,
  OrganizationStats,
  OrganizationActivity,
  Customer,
} from '@services/customer.service';
import { OrganizationTagsEditor } from '@components/customers/OrganizationTagsEditor';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import { ManagedByCrmNotice } from '@features/integrations/huhoka/ManagedByCrmNotice';

// Helper functions
function getTierBadgeColor(tier?: string): string {
  switch (tier) {
    case 'enterprise':
      return 'bg-purple-100 text-purple-800';
    case 'premium':
      return 'bg-amber-100 text-amber-800';
    case 'vip':
      return 'bg-rose-100 text-rose-800';
    case 'basic':
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
  }
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
}

// Ticket type for organization tickets
interface OrganizationTicket {
  id: string;
  subject: string;
  status: string;
  priority: string;
  createdAt: string;
  updatedAt: string;
  customer?: { name: string; email: string };
}

// Sub-components
function StatCard({
  label,
  value,
  icon: Icon,
  trend,
  color = 'gray',
}: {
  label: string;
  value: string | number;
  icon: React.ElementType;
  trend?: { value: number; positive: boolean };
  color?: string;
}) {
  const colorClasses: Record<string, string> = {
    gray: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        {trend && (
          <span
            className={`text-xs font-medium ${
              trend.positive ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {trend.positive ? '+' : '-'}{trend.value}%
          </span>
        )}
      </div>
      <div className="mt-3">
        <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{value}</p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      </div>
    </Card>
  );
}

function CustomerRow({ customer }: { customer: Customer }) {
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3">
        <Link to={`/customers/${customer.id}`} className="flex items-center gap-3 group">
          <Avatar name={customer.name || customer.email} size="sm" />
          <div>
            <p className="text-sm font-medium text-gray-900 group-hover:text-primary-600 dark:text-gray-100">
              {customer.name || 'Unknown'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{customer.email}</p>
          </div>
        </Link>
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{customer.phone || '-'}</td>
      <td className="px-4 py-3">
        <Badge variant={customer.status === 'active' ? 'success' : 'default'} size="sm">
          {customer.status}
        </Badge>
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
        {customer.lastSeenAt ? formatRelativeTime(customer.lastSeenAt) : 'Never'}
      </td>
      <td className="px-4 py-3">
        <div className="flex gap-1">
          {customer.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
            >
              {tag}
            </span>
          ))}
          {customer.tags.length > 2 && (
            <span className="text-xs text-gray-400 dark:text-gray-500">+{customer.tags.length - 2}</span>
          )}
        </div>
      </td>
    </tr>
  );
}

function TicketRow({ ticket }: { ticket: OrganizationTicket }) {
  const statusColors: Record<string, string> = {
    open: 'bg-blue-100 text-blue-800',
    pending: 'bg-yellow-100 text-yellow-800',
    solved: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  };

  const priorityColors: Record<string, string> = {
    urgent: 'text-red-600',
    high: 'text-orange-600',
    medium: 'text-yellow-600',
    low: 'text-gray-600 dark:text-gray-400',
  };

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3">
        <Link to={`/tickets/${ticket.id}`} className="group">
          <p className="text-sm font-medium text-gray-900 group-hover:text-primary-600 dark:text-gray-100">
            {ticket.subject}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">#{ticket.id}</p>
        </Link>
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{ticket.customer?.name}</td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[ticket.status]}`}>
          {ticket.status}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className={`text-sm font-medium ${priorityColors[ticket.priority]}`}>
          {ticket.priority}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
        {formatRelativeTime(ticket.createdAt)}
      </td>
    </tr>
  );
}

function ActivityItem({ activity }: { activity: OrganizationActivity }) {
  const getIcon = () => {
    switch (activity.type) {
      case 'ticket_created':
        return <Ticket className="h-4 w-4 text-blue-500" />;
      case 'ticket_resolved':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'customer_added':
        return <Users className="h-4 w-4 text-purple-500" />;
      default:
        return <Activity className="h-4 w-4 text-gray-500 dark:text-gray-400" />;
    }
  };

  return (
    <div className="flex gap-3 py-3">
      <div className="flex-shrink-0 mt-0.5">{getIcon()}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900 dark:text-gray-100">{activity.description}</p>
        <p className="text-xs text-gray-500 mt-0.5 dark:text-gray-400">
          {activity.performedBy?.name && `by ${activity.performedBy.name} • `}
          {formatRelativeTime(activity.occurredAt)}
        </p>
      </div>
    </div>
  );
}

function SubsidiaryCard({ organization }: { organization: Organization }) {
  return (
    <Link to={`/organizations/${organization.id}`}>
      <Card className="p-4 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center dark:bg-gray-800">
              <Building2 className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{organization.name}</p>
              {organization.domain && (
                <p className="text-xs text-gray-500 dark:text-gray-400">{organization.domain}</p>
              )}
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-500" />
        </div>
        <div className="mt-4 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1">
            <Users className="h-3.5 w-3.5" />
            {organization.customerCount || 0} customers
          </span>
          <span className="flex items-center gap-1">
            <Ticket className="h-3.5 w-3.5" />
            {organization.ticketCount || 0} tickets
          </span>
        </div>
      </Card>
    </Link>
  );
}

// Main component
export default function OrganizationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [stats, setStats] = useState<OrganizationStats | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customersTotal, setCustomersTotal] = useState(0);
  const [tickets, setTickets] = useState<OrganizationTicket[]>([]);
  const [ticketsTotal, setTicketsTotal] = useState(0);
  const [activities, setActivities] = useState<OrganizationActivity[]>([]);
  const [subsidiaries, setSubsidiaries] = useState<Organization[]>([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerPage, setCustomerPage] = useState(1);
  const [ticketPage, setTicketPage] = useState(1);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadOrganization = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch organization details
      const org = await organizationService.getOrganization(id);
      setOrganization(org);

      // Fetch organization stats
      try {
        const orgStats = await organizationService.getOrganizationStats(id);
        setStats(orgStats);
      } catch (err) {
        console.error('Failed to fetch organization stats:', err);
        setStats({
          totalCustomers: org.customerCount || 0,
          activeCustomers: 0,
          totalTickets: org.ticketCount || 0,
          openTickets: 0,
        });
      }

      // Fetch organization customers
      try {
        const customersResponse = await organizationService.getOrganizationCustomers(id, {
          page: customerPage,
          limit: 10,
          search: customerSearch || undefined,
        });
        setCustomers(customersResponse.data);
        setCustomersTotal(customersResponse.total);
      } catch (err) {
        console.error('Failed to fetch organization customers:', err);
        setCustomers([]);
        setCustomersTotal(0);
      }

      // Fetch organization tickets
      try {
        const ticketsResponse = await organizationService.getOrganizationTickets(id, {
          page: ticketPage,
          limit: 10,
        });
        setTickets(ticketsResponse.data || []);
        setTicketsTotal(ticketsResponse.total || 0);
      } catch (err) {
        console.error('Failed to fetch organization tickets:', err);
        setTickets([]);
        setTicketsTotal(0);
      }

      // Fetch organization activity
      try {
        const activityResponse = await organizationService.getOrganizationActivity(id, { limit: 10 });
        setActivities(activityResponse.items || []);
      } catch (err) {
        console.error('Failed to fetch organization activity:', err);
        setActivities([]);
      }

      // Fetch child organizations (subsidiaries)
      try {
        const children = await organizationService.getChildOrganizations(id);
        setSubsidiaries(children || []);
      } catch (err) {
        console.error('Failed to fetch subsidiaries:', err);
        setSubsidiaries([]);
      }
    } catch (err) {
      setError('Failed to load organization details');
      console.error('Error loading organization:', err);
    } finally {
      setIsLoading(false);
    }
  }, [id, customerPage, customerSearch, ticketPage]);

  useEffect(() => {
    loadOrganization();
  }, [loadOrganization]);

  const handleDelete = async () => {
    if (!id) return;

    try {
      await organizationService.deleteOrganization(id);
      navigate('/organizations');
    } catch (err) {
      console.error('Error deleting organization:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !organization) {
    return (
      <div className="h-full flex items-center justify-center">
        <EmptyState
          icon={<AlertCircle className="h-12 w-12" />}
          title="Organization not found"
          description={error || "The organization you're looking for doesn't exist or has been deleted."}
          action={
            <Link to="/organizations">
              <Button>Back to Organizations</Button>
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header — floating card: identity + utility cluster, then actions */}
      <div className="flex-shrink-0 px-4 pt-4">
        <div
          className="border border-[var(--co-line)] bg-[var(--co-raised)] px-[18px] py-4"
          style={{ borderRadius: 14, boxShadow: 'var(--co-shadow-card)' }}
        >
          <div className="flex items-center justify-between gap-[10px]">
            <div className="flex min-w-0 items-center gap-4">
              <Link to="/organizations">
                <Button variant="ghost" size="sm" className="!rounded-[10px]">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </Link>
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-xl bg-gray-100 flex items-center justify-center dark:bg-gray-800">
                  <Building2 className="h-7 w-7 text-gray-500 dark:text-gray-400" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-3">
                    <h1 className="text-xl font-semibold text-[var(--co-ink)]">{organization.name}</h1>
                    <Badge variant={organization.status === 'active' ? 'success' : 'default'}>
                      {organization.status}
                    </Badge>
                    {organization.tier && (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTierBadgeColor(organization.tier)}`}>
                        {organization.tier}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-4 mt-1 text-sm text-[var(--co-ink-3)]">
                    {organization.domain && (
                      <a
                        href={`https://${organization.domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 hover:text-primary-600"
                      >
                        <Globe className="h-4 w-4" />
                        {organization.domain}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                    {organization.industry && <span>{organization.industry}</span>}
                    {organization.size && <span>{organization.size} employees</span>}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex h-[34px] flex-none items-center">
              <PageHeaderMeta />
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-[10px] border-t border-[var(--co-line-subtle)] pt-3">
              <div className="flex-1" />
              <Button variant="outline" className="!rounded-[10px]">
                <Edit2 className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Dropdown
                trigger={
                  <Button variant="outline" size="sm" className="!rounded-[10px]">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                }
                placement="bottom-end"
              >
                <MenuItem icon={<Plus className="h-4 w-4" />}>Add Customer</MenuItem>
                <MenuItem icon={<Ticket className="h-4 w-4" />}>Create Ticket</MenuItem>
                {/* A HuHoKa-managed organization is deleted (archived) in HuHoKa CRM. */}
                {!organization.managedBy && <MenuDivider />}
                {!organization.managedBy && (
                  <MenuItem
                    icon={<Trash2 className="h-4 w-4" />}
                    danger
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    Delete Organization
                  </MenuItem>
                )}
              </Dropdown>
          </div>
          <ManagedByCrmNotice
            managedBy={organization.managedBy}
            editUrl={organization.externalEditUrl}
            fields="The organization name"
            className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200"
          />
        </div>
      </div>

      {/* Tabs and Content */}
      <div className="flex-1 overflow-hidden">
        <Tabs defaultTab="overview" className="h-full flex flex-col">
          <div className="flex-shrink-0 px-6 bg-white border-b border-gray-200 dark:bg-gray-800 dark:border-gray-700">
            <TabList>
              <Tab id="overview">Overview</Tab>
              <Tab id="customers">
                Customers
                <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 rounded-full dark:bg-gray-800">
                  {customersTotal}
                </span>
              </Tab>
              <Tab id="tickets">
                Tickets
                <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 rounded-full dark:bg-gray-800">
                  {ticketsTotal}
                </span>
              </Tab>
              <Tab id="activity">Activity</Tab>
              <Tab id="subsidiaries">
                Subsidiaries
                {subsidiaries.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 rounded-full dark:bg-gray-800">
                    {subsidiaries.length}
                  </span>
                )}
              </Tab>
            </TabList>
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* Overview Tab */}
            <TabPanel id="overview" className="p-6">
              <div className="grid grid-cols-4 gap-4 mb-6">
                <StatCard
                  label="Total Customers"
                  value={stats?.totalCustomers || 0}
                  icon={Users}
                  color="blue"
                />
                <StatCard
                  label="Open Tickets"
                  value={stats?.openTickets || 0}
                  icon={Ticket}
                  color="yellow"
                />
                <StatCard
                  label="Avg Response Time"
                  value={stats?.avgResponseTime ? `${stats.avgResponseTime}h` : '-'}
                  icon={Clock}
                  color="green"
                />
                <StatCard
                  label="SLA Compliance"
                  value={stats?.slaComplianceRate ? `${stats.slaComplianceRate}%` : '-'}
                  icon={TrendingUp}
                  color="purple"
                  trend={{ value: 5, positive: true }}
                />
              </div>

              <div className="grid grid-cols-3 gap-6">
                {/* Organization Details */}
                <Card className="col-span-2 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 dark:text-gray-100">Organization Details</h3>
                  <dl className="grid grid-cols-2 gap-4">
                    {organization.website && (
                      <div>
                        <dt className="text-sm text-gray-500 dark:text-gray-400">Website</dt>
                        <dd className="mt-1">
                          <a
                            href={organization.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary-600 hover:underline flex items-center gap-1"
                          >
                            {organization.website}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </dd>
                      </div>
                    )}
                    {organization.phone && (
                      <div>
                        <dt className="text-sm text-gray-500 dark:text-gray-400">Phone</dt>
                        <dd className="mt-1 text-sm text-gray-900 flex items-center gap-2 dark:text-gray-100">
                          <Phone className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                          {organization.phone}
                        </dd>
                      </div>
                    )}
                    {organization.address && (
                      <div className="col-span-2">
                        <dt className="text-sm text-gray-500 dark:text-gray-400">Address</dt>
                        <dd className="mt-1 text-sm text-gray-900 flex items-start gap-2 dark:text-gray-100">
                          <MapPin className="h-4 w-4 text-gray-400 mt-0.5 dark:text-gray-500" />
                          <span>
                            {[
                              organization.address.street,
                              organization.address.city,
                              organization.address.state,
                              organization.address.postalCode,
                              organization.address.country,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </span>
                        </dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-sm text-gray-500 dark:text-gray-400">Created</dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                        {formatDate(organization.createdAt)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500 dark:text-gray-400">Last Updated</dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                        {formatRelativeTime(organization.updatedAt)}
                      </dd>
                    </div>
                  </dl>

                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                      <h4 className="text-sm font-medium text-gray-700 mb-2 dark:text-gray-200">Tags</h4>
                      <OrganizationTagsEditor
                        organizationId={organization.id}
                        initialTags={organization.tags}
                        onTagsChange={(tags) => {
                          setOrganization({ ...organization, tags });
                        }}
                        mode="inline"
                        allowCreate
                        showCounts
                      />
                    </div>

                  {organization.notes && (
                    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                      <h4 className="text-sm font-medium text-gray-700 mb-2 dark:text-gray-200">Notes</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{organization.notes}</p>
                    </div>
                  )}
                </Card>

                {/* Recent Activity */}
                <Card className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 dark:text-gray-100">Recent Activity</h3>
                  {activities.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No recent activity</p>
                  ) : (
                    <div className="divide-y divide-gray-100 dark:divide-gray-800">
                      {activities.slice(0, 5).map((activity) => (
                        <ActivityItem key={activity.id} activity={activity} />
                      ))}
                    </div>
                  )}
                </Card>
              </div>
            </TabPanel>

            {/* Customers Tab */}
            <TabPanel id="customers" className="p-6">
              <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between dark:border-gray-700">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <input
                      type="text"
                      placeholder="Search customers..."
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                    />
                  </div>
                  <Button size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Customer
                  </Button>
                </div>

                {customers.length === 0 ? (
                  <div className="py-12">
                    <EmptyState
                      icon={<Users className="h-12 w-12" />}
                      title="No customers"
                      description="This organization doesn't have any customers yet."
                      action={
                        <Button>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Customer
                        </Button>
                      }
                    />
                  </div>
                ) : (
                  <>
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-900">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                            Customer
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                            Phone
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                            Status
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                            Last Seen
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                            Tags
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {customers.map((customer) => (
                          <CustomerRow key={customer.id} customer={customer} />
                        ))}
                      </tbody>
                    </table>

                    {customersTotal > 10 && (
                      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                        <Pagination
                          currentPage={customerPage}
                          totalPages={Math.ceil(customersTotal / 10)}
                          onPageChange={setCustomerPage}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </TabPanel>

            {/* Tickets Tab */}
            <TabPanel id="tickets" className="p-6">
              <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm">All</Button>
                    <Button variant="ghost" size="sm">Open</Button>
                    <Button variant="ghost" size="sm">Pending</Button>
                    <Button variant="ghost" size="sm">Solved</Button>
                  </div>
                  <Button size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    New Ticket
                  </Button>
                </div>

                {tickets.length === 0 ? (
                  <div className="py-12">
                    <EmptyState
                      icon={<Ticket className="h-12 w-12" />}
                      title="No tickets"
                      description="This organization doesn't have any tickets yet."
                      action={
                        <Button>
                          <Plus className="h-4 w-4 mr-2" />
                          Create Ticket
                        </Button>
                      }
                    />
                  </div>
                ) : (
                  <>
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-900">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                            Subject
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                            Customer
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                            Status
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                            Priority
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                            Created
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {tickets.map((ticket) => (
                          <TicketRow key={ticket.id} ticket={ticket} />
                        ))}
                      </tbody>
                    </table>

                    {ticketsTotal > 10 && (
                      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                        <Pagination
                          currentPage={ticketPage}
                          totalPages={Math.ceil(ticketsTotal / 10)}
                          onPageChange={setTicketPage}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </TabPanel>

            {/* Activity Tab */}
            <TabPanel id="activity" className="p-6">
              <Card className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 dark:text-gray-100">Activity Timeline</h3>
                {activities.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No activity recorded</p>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-gray-800">
                    {activities.map((activity) => (
                      <ActivityItem key={activity.id} activity={activity} />
                    ))}
                  </div>
                )}
              </Card>
            </TabPanel>

            {/* Subsidiaries Tab */}
            <TabPanel id="subsidiaries" className="p-6">
              {subsidiaries.length === 0 ? (
                <EmptyState
                  icon={<Building2 className="h-12 w-12" />}
                  title="No subsidiaries"
                  description="This organization doesn't have any subsidiary organizations."
                  action={
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Subsidiary
                    </Button>
                  }
                />
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {subsidiaries.map((org) => (
                    <SubsidiaryCard key={org.id} organization={org} />
                  ))}
                </div>
              )}
            </TabPanel>
          </div>
        </Tabs>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Delete Organization"
        message={`Are you sure you want to delete "${organization.name}"? This action cannot be undone. All customers will be disassociated from this organization.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
}
